import {getTestProps} from '@benchling/js-test-utils';
import {compact} from '@benchling/js-utils';
import {css} from 'aphrodite';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  Background,
  Edge,
  OnConnect,
  OnConnectStartParams,
  ReactFlow,
  useReactFlow,
  useStore,
  type Connection,
} from 'reactflow';

import {assert} from 'Asserts';
import {
  ProcessFlowchartNodeDetailsType,
  ProcessFlowchartPlatformFeatureType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {SchemaById} from 'lookup-config/LookupConfigModalConstants';
import ReactFlowWrapper from 'processes/graph-builder/flowchart-viz/ReactFlowWrapper';
import {isValidConnection} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/edges/connectionValidationUtils';
import {
  HandlesContext,
  HandlesContextValue,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/HandlesContext';
import {ProcessFlowchartTaskSchemaNodeAdapter} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartNodeAdapter';
import {
  NodesContext,
  NodesContextValue,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/NodesContext';
import {ProcessFlowchartEditorConfigurator} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {DEFAULT_FIT_VIEW_OPTIONS} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import {
  PROCESS_FLOWCHART_EDGE_LABEL_SIZE,
  PROCESS_FLOWCHART_EDGE_LABEL_WEIGHT,
} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';
import {ProcessFlowchartValidationError} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {parseHandleId} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/flowchartIdentifierUtils';
import {FONT_FAMILY_PROXIMA} from 'styles/FontConstants';
import useStateIfMounted from 'ui-library/hooks/useStateIfMounted';
import DefaultBackground from 'ui-library/reactflow/backgrounds/DefaultBackground';
import {ControlledAutoLayoutButton} from 'ui-library/reactflow/controls/AutoLayoutControlButton';
import HorizontalControls, {FIT_VIEW_MAX_ZOOM} from 'ui-library/reactflow/controls/HorizontalControls';
import {useGetNodeHandlesData} from 'ui-library/reactflow/handles/useGetNodeHandlesData';
import {LayoutAlgorithmName} from 'ui-library/reactflow/layout/algorithms';
import {LayoutSettings} from 'ui-library/reactflow/layout/calculateDimensionsForLayout';
import layoutFlowchart from 'ui-library/reactflow/layout/layoutFlowchart';
import {useDefaultReactFlowProps} from 'ui-library/reactflow/ReactFlowConstants';
import {SidepanelSize} from 'ui-library/reactflow/sidepanels/FlowchartContextPanel';
import {FLOWCHART_SIDE_PANEL_WIDTH} from 'ui-library/reactflow/sidepanels/FlowchartSidebar';
import {getTextDimensions} from 'util/getTextDimensions';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {CustomMappingContext, CustomMappingContextValue} from './CustomMappingContext';
import {
  CustomMappingData,
  DEFAULT_FLOWCHART_PROPS,
  EDITABLE_FLOWCHART_PROPS,
  PROCESS_FLOWCHART_CONFIG_EDITOR_STYLES,
  READ_ONLY_FLOWCHART_PROPS,
  SNAP_GRID,
  StaticTemplateFlowchart,
  TaskDefinitionVersionSpecWithTaskSchemaFlowchart,
  TaskSchemaNodeSectionType,
  TransitionSource,
  UserFacingNodeTypes,
} from './processFlowchartConfigEditorConstants';
import ProcessFlowchartConfigEditorLeftSidepanel, {
  LEFT_SIDEPANEL_WIDTH,
} from './ProcessFlowchartConfigEditorLeftSidepanel';
import {convertStaticFlowchartToSpecFlowchartWithTaskSchema} from './processFlowchartConfigEditorNetworkUtils';
import {
  FlowchartNode,
  isInteractivePlaceholderNode,
  isInteractiveTaskNode,
  isStaticTaskSchemaNodeData,
  TaskDefinitionVersionSpecWithTaskSchema,
  TaskSchemaNode,
  TaskSchemaStaticNodeData,
} from './processFlowchartConfigEditorNodeTypes';
import {
  PROCESS_FLOWCHART_CONFIG_EDITOR_EDGE_TYPES,
  PROCESS_FLOWCHART_CONFIG_EDITOR_NODE_TYPES,
  PROCESS_FLOWCHART_CONFIG_EDITOR_NODE_TYPES_BY_EXECUTION_TYPE,
  PROCESS_FLOWCHART_CONFIG_EDITOR_NODE_TYPES_BY_PLATFORM_FEATURE,
} from './processFlowchartConfigEditorRegistry';
import ProcessFlowchartConfigEditorRightSidepanel from './ProcessFlowchartConfigEditorRightSidepanel';
import {
  FlowchartCustomMappingDataChangeType,
  FlowchartNodeChange,
  FlowchartNodeChangeType,
  FlowchartTransitionSourceChangeType,
} from './processFlowchartConfigEditorUtils';
import ProcessFlowchartValidationErrorBanner from './ProcessFlowchartValidationErrorBanner';
import {FlowchartConfigWithMetadata} from './redux/flowchartConfigAutosaveManagerCallbacks';
import {FlowchartConfigWithPartialMetadata, LiveStateChanges} from './redux/FlowchartConfigStateManager';
import {
  FlowchartConfigAutosaverPausedCondition,
  FlowchartConfigMetadata,
} from './redux/reduceFlowchartConfig';
import {SingletonTaskSchemasContext} from './SingletonTaskSchemasContext';
import {ConfigurableTaskDefinitionGQL} from './useAllSchemasById';
import useFlowchartState from './useFlowchartState/useFlowchartState';

export interface ProcessFlowchartConfigEditorContentProps {
  flowchart: StaticTemplateFlowchart;
  flowchartEditorConfigurator: ProcessFlowchartEditorConfigurator;
  allSchemasById: SchemaById;
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  flowchartTaskDefinitionId: string;
  taskDefinitionsForSidepanel: Array<ConfigurableTaskDefinitionGQL>;
  customMappingDataByEdgeId: Record<string, CustomMappingData>;
  transitionSourceByNodeIdPair: Record<string, TransitionSource>;
  didInitialConfigHaveConditionalMapping: boolean;
  didInitialFlowchartHaveChainedRouters: boolean;
  refetchFlowchartConfig?: () => void;
  liveStateChanges: LiveStateChanges;
  locked: boolean;
  validationErrors: Array<ProcessFlowchartValidationError>;
  draftFlowchartConfigAndMetadata: FlowchartConfigWithMetadata;
  setDraftFlowchartConfigAndMetadata: (
    getNewFlowchartConfigAndMetadata: (
      currentFlowchartConfigAndMetadata: FlowchartConfigWithMetadata
    ) => FlowchartConfigWithPartialMetadata,
    saveImmediately?: boolean,
    skipSettingDirtyState?: boolean
  ) => Promise<FlowchartConfigMetadata | null>;
}

function getAllowedNodeTypes(
  flowchartEditorConfigurator: ProcessFlowchartEditorConfigurator
): Array<UserFacingNodeTypes> {
  const allowedTaskNodeTypes = compact(
    flowchartEditorConfigurator.allowedNodeExecutionTypes.map(
      (executionType) => PROCESS_FLOWCHART_CONFIG_EDITOR_NODE_TYPES_BY_EXECUTION_TYPE[executionType]
    )
  );
  const allowedNodeTypesForPlatformFeatures = compact(
    Object.entries(PROCESS_FLOWCHART_CONFIG_EDITOR_NODE_TYPES_BY_PLATFORM_FEATURE)
      .filter(([feature]) =>
        flowchartEditorConfigurator.hasFeature(feature as ProcessFlowchartPlatformFeatureType)
      )
      .map(([_, nodeType]) => nodeType)
  );
  return [...allowedTaskNodeTypes, ...allowedNodeTypesForPlatformFeatures];
}

function getAdapterForNode(
  node: FlowchartNode | null
): ProcessFlowchartTaskSchemaNodeAdapter<unknown, unknown> | null {
  if (node && isInteractiveTaskNode(node)) {
    return PLUGIN_REGISTRY_INSTANCE.getTaskSchemaNodeAdapter(
      node.data.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema.executionType
    );
  }
  return null;
}

/**
 * Horizontal flowchart config editor content that displays the specified flowchart config version.
 *
 * Converts static nodes and edges to interactive nodes and edges.
 */
export default function ProcessFlowchartConfigEditorContent(
  props: ProcessFlowchartConfigEditorContentProps
): JSX.Element {
  const defaultReactFlowProps = useDefaultReactFlowProps();
  const interactionProps = props.locked ? READ_ONLY_FLOWCHART_PROPS : EDITABLE_FLOWCHART_PROPS;
  const singletonTaskSchemasByName = SingletonTaskSchemasContext.useContext();

  const allowedNodeTypes = useMemo(
    () => getAllowedNodeTypes(props.flowchartEditorConfigurator),
    [props.flowchartEditorConfigurator]
  );

  const [_isAutosaverPaused, setIsAutosaverPaused] = props.liveStateChanges.isAutosaverPausedState;

  const reactFlowInstance = useReactFlow();
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const leftSidepanelRef = useRef<HTMLDivElement>(null);
  const rightSidepanelRef = useRef<HTMLDivElement>(null);

  const addNodeParams = useMemo(
    () => ({
      flowchartEditorConfigurator: props.flowchartEditorConfigurator,
      rootTaskSchema: props.rootTaskSchema,
      taskDefinitionsForSidepanel: props.taskDefinitionsForSidepanel,
      didInitialFlowchartHaveChainedRouters: props.didInitialFlowchartHaveChainedRouters,
      singletonTaskSchemasByName,
    }),
    [
      props.flowchartEditorConfigurator,
      props.rootTaskSchema,
      props.taskDefinitionsForSidepanel,
      props.didInitialFlowchartHaveChainedRouters,
      singletonTaskSchemasByName,
    ]
  );

  const {
    interactiveNodes,
    interactiveEdges,
    applyFlowchartChanges,
    onReactFlowNodeChangesOnly,
    onReactFlowEdgeChangesOnly,
    onConnect,
    setNodesAndEdgesViaChangesForLayout,
    // Sidepanel state
    isLeftSidepanelOpen,
    setIsLeftSidepanelOpen,
    isRightSidepanelOpen,
    selectedFieldId,
    selectedNodeIdForRightSidepanel,
    setSelectedFieldId,
    // Sidepanel functions
    handleRightSidepanelClose,
    revertNodeSelection,
    // Node addition state
    interactiveFlowchartOptions,
    setPlaceholderNodeBeingAdded,
    // Node deletion state
    nodesBeingDeletedRef,
    // Node dimensions
    getMeasuredDimensionsFromStore,
  } = useFlowchartState({
    staticFlowchart: props.flowchart,
    flowchartConfigMetadata: props.draftFlowchartConfigAndMetadata.metadata,
    validationErrors: props.validationErrors,
    setFlowchartAndMetadata: props.setDraftFlowchartConfigAndMetadata,
    addNodeParams,
    reactFlowInstance,
    reactFlowWrapper,
    leftSidepanelRef,
    rightSidepanelRef,
  });

  const [connectionDragState, setConnectionDragState] =
    useState<HandlesContextValue['connectionDragState']>(null);

  const hasPerformedInitialAutolayout = useRef(false);

  const [performInitialFitView, setPerformInitialFitView] = useStateIfMounted(false);

  // Record the start and end nodes for use in layout.
  const startNode = useMemo(
    () =>
      interactiveNodes.find(
        (node): node is TaskSchemaNode =>
          isInteractiveTaskNode(node) &&
          node.data.nodeDetailsType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
      ) ?? null,
    [interactiveNodes]
  );

  const endNode = useMemo(
    () =>
      interactiveNodes.find(
        (node): node is TaskSchemaNode =>
          isInteractiveTaskNode(node) &&
          node.data.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS
      ) ?? null,
    [interactiveNodes]
  );

  const getHandles = useGetNodeHandlesData();

  const allNodesReadyForAutolayout = useStore(
    useCallback(() => {
      if (hasPerformedInitialAutolayout.current || interactiveNodes.length === 0) {
        return false;
      }

      // Check if all nodes have handles. In testing we found that on mount the reactflow nodes
      // are not always finished initializing. Checking handles is a proxy for us to determine if node
      // state is fully initialized.
      const nodesWithoutHandles = interactiveNodes.filter((node) => getHandles(node.id).length === 0);

      return nodesWithoutHandles.length === 0;
    }, [interactiveNodes, getHandles])
  );

  // Used by layout system to get the dimensions of edge labels.
  // todo(BNCH-137336): Possibly make this method for calculating label dimensions the default
  // for our internal ReactFlow library.
  const calculateEdgeLabelDimensions = useCallback((edge: Edge): {width: number; height: number} => {
    if (edge.label == null || typeof edge.label !== 'string') {
      return {width: 0, height: 0};
    }
    const {width, height} = getTextDimensions(
      edge.label,
      PROCESS_FLOWCHART_EDGE_LABEL_WEIGHT,
      PROCESS_FLOWCHART_EDGE_LABEL_SIZE,
      FONT_FAMILY_PROXIMA
    );

    return {
      width,
      height,
    };
  }, []);

  const getCustomLayoutSettings = useCallback(
    (): Partial<LayoutSettings<FlowchartNode, Edge>> => ({
      snapToGrid: SNAP_GRID,
      algorithm: LayoutAlgorithmName.ELK,
      startNodeId: startNode?.id,
      endNodeId: endNode?.id,
      calculateEdgeLabelDimensions,
      calculateNodeDimensions: (node) => getMeasuredDimensionsFromStore(node.id),
      horizontalSpaceBetweenNodes: 30,
    }),
    [startNode, endNode, calculateEdgeLabelDimensions, getMeasuredDimensionsFromStore]
  );

  useEffect(() => {
    // Only perform initial autolayout once, not every time nodes change
    if (interactiveNodes.length === 0 || hasPerformedInitialAutolayout.current) {
      return;
    }

    // Wait for all nodes to be ready (handles + dimensions) before running autolayout.
    if (!allNodesReadyForAutolayout) {
      return;
    }

    const customLayoutSettings = getCustomLayoutSettings();

    layoutFlowchart(
      {
        nodes: interactiveNodes,
        edges: interactiveEdges,
        getHandles,
      },
      customLayoutSettings
    ).then(({nodes: layoutNodes, edges: layoutEdges}) => {
      setNodesAndEdgesViaChangesForLayout(layoutNodes, layoutEdges);
      hasPerformedInitialAutolayout.current = true;
      setPerformInitialFitView(true);
    });
  }, [
    interactiveNodes,
    interactiveEdges,
    allNodesReadyForAutolayout,
    setNodesAndEdgesViaChangesForLayout,
    setPerformInitialFitView,
    getHandles,
    getCustomLayoutSettings,
  ]);

  useEffect(() => {
    if (!performInitialFitView) {
      return;
    }
    setTimeout(() => {
      reactFlowInstance.fitView({...DEFAULT_FIT_VIEW_OPTIONS, maxZoom: FIT_VIEW_MAX_ZOOM});
    });
    setPerformInitialFitView(false);
  }, [performInitialFitView, reactFlowInstance, setPerformInitialFitView]);

  const handleConnectStart = useCallback(
    (event: React.MouseEvent | React.TouchEvent, params: OnConnectStartParams): void => {
      if (!params.handleId || !params.nodeId) {
        return;
      }

      const sourceHandleInfo = parseHandleId(params.handleId);
      setConnectionDragState({
        isDragging: true,
        sourceHandleInfo,
      });
    },
    []
  );

  const handleConnectEnd = useCallback((): void => {
    setConnectionDragState(null);
  }, []);
  const handleConnect: OnConnect = useCallback(
    (params: Connection) => {
      setConnectionDragState(null);

      if (
        params.source == null ||
        params.target == null ||
        params.sourceHandle == null ||
        params.targetHandle == null
      ) {
        return;
      }
      onConnect(params);
    },
    [onConnect]
  );

  const dragOverHandler = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
  };

  const hasPlaceholderNodes = useMemo(
    () => interactiveNodes.some((node) => isInteractivePlaceholderNode(node)),
    [interactiveNodes]
  );

  useEffect(() => {
    setIsAutosaverPaused(FlowchartConfigAutosaverPausedCondition.HAS_PLACEHOLDER_NODE, hasPlaceholderNodes);
  }, [hasPlaceholderNodes, setIsAutosaverPaused]);

  const taskDefinitionVersionBasedFlowchart = useMemo(
    () => convertStaticFlowchartToSpecFlowchartWithTaskSchema(props.flowchart),
    [props.flowchart]
  );

  // Memoize the selected node to prevent unnecessary recalculations
  const selectedNode = useMemo(
    () =>
      selectedNodeIdForRightSidepanel
        ? (interactiveNodes.find((node) => node.id === selectedNodeIdForRightSidepanel) ?? null)
        : null,
    [selectedNodeIdForRightSidepanel, interactiveNodes]
  );

  const handlesContext: HandlesContextValue = useMemo(
    () => ({
      connectionDragState,
    }),
    [connectionDragState]
  );

  const validateConnection = useCallback(
    (connection: Connection): boolean => {
      const getAdapterForNodeId = (
        nodeId: string
      ): ProcessFlowchartTaskSchemaNodeAdapter<unknown, unknown> | null => {
        const node = interactiveNodes.find((n) => n.id === nodeId) ?? null;
        return getAdapterForNode(node);
      };

      const getNodeData = (nodeId: string): {nodeDetailsType?: ProcessFlowchartNodeDetailsType} | null => {
        const node = interactiveNodes.find((n) => n.id === nodeId);
        if (!node) {
          return null;
        }

        if (isInteractiveTaskNode(node)) {
          return {nodeDetailsType: node.data.nodeDetailsType};
        }
        return null;
      };

      return isValidConnection(connection, interactiveEdges, getNodeData, getAdapterForNodeId);
    },
    [interactiveEdges, interactiveNodes]
  );

  const nodesContext: NodesContextValue = useMemo(
    () => ({
      nodes: interactiveNodes,
      startNode,
      endNode,
    }),
    [interactiveNodes, startNode, endNode]
  );

  const customMappingContext: CustomMappingContextValue = props.customMappingDataByEdgeId;

  // Updating custom mapping data for an edge will automatically update lookup config through the context
  const onChangeCustomMappingData = useCallback(
    (edgeId, newCustomMappingData) => {
      applyFlowchartChanges((flowchartDraftState) => ({
        customMappingChanges: [
          {
            type: FlowchartCustomMappingDataChangeType.REPLACE,
            id: edgeId,
            customMapping: newCustomMappingData,
          },
        ],
      }));
    },
    [applyFlowchartChanges]
  );

  const onChangeTransitionSource = useCallback(
    (nodeIdPair: string, newTransitionSource: TransitionSource) => {
      applyFlowchartChanges((flowchartDraftState) => ({
        transitionSourceChanges: [
          {
            type: FlowchartTransitionSourceChangeType.REPLACE,
            id: nodeIdPair,
            transitionSource: newTransitionSource,
          },
        ],
      }));
    },
    [applyFlowchartChanges]
  );

  /**
   * Enables updates to a node from the right sidepanel to be saved immediately to the DB
   * without waiting for an auto save.
   *
   * If isBlocking is true, a network call will be made immediately to save the flowchart config.
   * If isBlocking is false, the node changes will be accumulated with other changes to wait for auto save.
   */
  const onUpdateTaskDefinitionVersion = useCallback(
    (
      updater: (
        currentFlowchart: TaskDefinitionVersionSpecWithTaskSchemaFlowchart
      ) => TaskDefinitionVersionSpecWithTaskSchema<unknown>,
      isBlocking: boolean
    ): Promise<FlowchartConfigMetadata | null> =>
      applyFlowchartChanges((flowchartDraftState) => {
        assert(
          selectedNodeIdForRightSidepanel != null,
          'Expected selectedNodeIdForRightSidepanel to be non-null'
        );

        const selectedNode = flowchartDraftState.staticFlowchart.nodesStaticData.find(
          (node) => node.id === selectedNodeIdForRightSidepanel
        );
        if (selectedNode == null) {
          return {};
        }
        assert(isStaticTaskSchemaNodeData(selectedNode), 'Expected node to be a static task node');

        const currentFlowchart = convertStaticFlowchartToSpecFlowchartWithTaskSchema(
          flowchartDraftState.staticFlowchart
        );
        const newTaskDefinitionVersionSpec = updater(currentFlowchart);

        // Find all nodes that share the same task definition version and update them all.
        // For singleton schemas (like procedure unit operations), all nodes share the same taskSchemaId
        // but have unique task definition versions, so we must match by the definition version ID.
        const taskDefinitionVersionClientId = selectedNode.taskDefinitionVersionSpec.clientId;
        const nodesToUpdate = flowchartDraftState.staticFlowchart.nodesStaticData.filter(
          (node): node is TaskSchemaStaticNodeData =>
            isStaticTaskSchemaNodeData(node) &&
            node.taskDefinitionVersionSpec.clientId === taskDefinitionVersionClientId
        );

        const adapter = PLUGIN_REGISTRY_INSTANCE.getTaskSchemaNodeAdapter(
          selectedNode.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema.executionType
        );

        // Create node changes for all matching nodes
        const nodeChanges: Array<FlowchartNodeChange> = nodesToUpdate.map((nodeToUpdate) => {
          const newSections = adapter.createSections(
            newTaskDefinitionVersionSpec,
            nodeToUpdate.nodeDetailsType
          );

          const oldMapFromStart = nodeToUpdate.sections.find(
            (section) => section.sectionType === TaskSchemaNodeSectionType.MAP_FROM_START
          );
          const newMapFromStart = newSections.find(
            (section) => section.sectionType === TaskSchemaNodeSectionType.MAP_FROM_START
          );
          if (oldMapFromStart && newMapFromStart) {
            newMapFromStart.sectionRows = oldMapFromStart.sectionRows;
          }

          return {
            type: FlowchartNodeChangeType.REPLACE as const,
            id: nodeToUpdate.id,
            data: {
              ...nodeToUpdate,
              taskDefinitionVersionSpec: newTaskDefinitionVersionSpec,
              nodeName: adapter.getNodeName(
                newTaskDefinitionVersionSpec,
                nodeToUpdate.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS
              ),
              sections: newSections,
            },
          };
        });

        return {nodeChanges};
      }, isBlocking),
    [selectedNodeIdForRightSidepanel, applyFlowchartChanges]
  );

  const adapterForSelectedNode = getAdapterForNode(selectedNode);

  const selectedFieldName = useMemo(() => {
    if (!selectedFieldId || !selectedNode) {
      return null;
    }

    const fieldName = adapterForSelectedNode?.getFieldNameFromFieldId(selectedNode, selectedFieldId);
    if (fieldName == null) {
      // If field was removed, close the sidepanel (ex: removed map from start field from node in canvas)
      handleRightSidepanelClose();
      return null;
    }
    return fieldName;
  }, [selectedFieldId, selectedNode, adapterForSelectedNode, handleRightSidepanelClose]);

  return (
    <div
      className={css(PROCESS_FLOWCHART_CONFIG_EDITOR_STYLES.flowchartEditorContentWrapper)}
      {...getTestProps({component: 'ProcessFlowchartConfigEditorContent', element: 'root'})}
    >
      <ReactFlowWrapper fullWidth={true}>
        <div
          className={css(PROCESS_FLOWCHART_CONFIG_EDITOR_STYLES.flowchartEditorContent)}
          ref={reactFlowWrapper}
        >
          <ProcessFlowchartConfigEditorLeftSidepanel
            isOpen={isLeftSidepanelOpen}
            setIsOpen={setIsLeftSidepanelOpen}
            taskDefinitionsForSidepanel={props.taskDefinitionsForSidepanel}
            allowedNodeTypes={allowedNodeTypes}
            applyFlowchartChanges={applyFlowchartChanges}
            setPlaceholderNodeBeingAdded={setPlaceholderNodeBeingAdded}
            options={interactiveFlowchartOptions}
            rootTaskSchema={props.rootTaskSchema}
            flowchartEditorConfigurator={props.flowchartEditorConfigurator}
            reactFlowWrapper={reactFlowWrapper}
            leftSidepanelRef={leftSidepanelRef}
            flowchartConfigMetadata={props.draftFlowchartConfigAndMetadata.metadata}
            metadataSidepanel={
              PLUGIN_REGISTRY_INSTANCE.getPluginForFlowchartType(
                props.flowchartEditorConfigurator.flowchartTypeName
              ).metadataEditor
            }
          />
          <div
            style={{
              flexGrow: 1,
              marginLeft: FLOWCHART_SIDE_PANEL_WIDTH,
              position: 'relative',
            }}
          >
            <ProcessFlowchartValidationErrorBanner validationErrors={props.validationErrors} />
            <HandlesContext.Provider value={handlesContext}>
              <NodesContext.Provider value={nodesContext}>
                <CustomMappingContext.Provider value={customMappingContext}>
                  <ReactFlow
                    {...defaultReactFlowProps}
                    {...DEFAULT_FLOWCHART_PROPS}
                    {...interactionProps}
                    nodes={interactiveNodes}
                    nodeTypes={PROCESS_FLOWCHART_CONFIG_EDITOR_NODE_TYPES}
                    edgeTypes={PROCESS_FLOWCHART_CONFIG_EDITOR_EDGE_TYPES}
                    edges={interactiveEdges}
                    onNodesChange={onReactFlowNodeChangesOnly}
                    onEdgesChange={onReactFlowEdgeChangesOnly}
                    onConnect={handleConnect}
                    onConnectStart={handleConnectStart}
                    onConnectEnd={handleConnectEnd}
                    onDragOver={dragOverHandler}
                    // ReactFlow v12 has onBeforeDelete, since we are on v11 we need deletes to go through our own handlers
                    // see handleDeleteKeyPress in useFlowchartState.ts for details
                    deleteKeyCode={null}
                    isValidConnection={validateConnection}
                    onlyRenderVisibleElements={true}
                  >
                    <Background gap={SNAP_GRID} />
                    <HorizontalControls
                      shiftLeft={isLeftSidepanelOpen ? LEFT_SIDEPANEL_WIDTH : 0}
                      additionalControls={
                        <ControlledAutoLayoutButton
                          flowchartData={{nodes: interactiveNodes, edges: interactiveEdges, getHandles}}
                          customLayoutSettings={getCustomLayoutSettings()}
                          setNodesAndEdges={setNodesAndEdgesViaChangesForLayout}
                        />
                      }
                    />
                    <DefaultBackground />
                  </ReactFlow>
                </CustomMappingContext.Provider>
              </NodesContext.Provider>
            </HandlesContext.Provider>
          </div>
          <ProcessFlowchartConfigEditorRightSidepanel
            rightSidepanelRef={rightSidepanelRef}
            flowchartEditorConfigurator={props.flowchartEditorConfigurator}
            selectedFieldId={selectedFieldId}
            onChangeSelectedFieldId={setSelectedFieldId}
            interactiveFlowchart={{nodes: interactiveNodes, edges: interactiveEdges}}
            flowchart={taskDefinitionVersionBasedFlowchart}
            selectedNode={selectedNode}
            size={adapterForSelectedNode?.getDisplayOptions().rightSidepanelSize ?? SidepanelSize.MEDIUM}
            isOpen={isRightSidepanelOpen}
            titleOverride={adapterForSelectedNode
              ?.getDisplayOptions()
              .rightSidepanelNameOverride?.(selectedNode?.data.nodeName ?? '', selectedFieldName)}
            onClose={handleRightSidepanelClose}
            onRevertNodeSelection={revertNodeSelection}
            nodesBeingDeletedRef={nodesBeingDeletedRef}
            onUpdateTaskDefinitionVersion={onUpdateTaskDefinitionVersion}
            interactiveFlowchartOptions={interactiveFlowchartOptions}
            didInitialConfigHaveConditionalMapping={props.didInitialConfigHaveConditionalMapping}
            didInitialFlowchartHaveChainedRouters={props.didInitialFlowchartHaveChainedRouters}
            allSchemasById={props.allSchemasById}
            flowchartTaskSchema={props.rootTaskSchema}
            flowchartTaskDefinitionId={props.flowchartTaskDefinitionId}
            customMappingDataByEdgeId={props.customMappingDataByEdgeId}
            onChangeCustomMappingData={onChangeCustomMappingData}
            transitionSourceByNodeIdPair={props.transitionSourceByNodeIdPair}
            onChangeTransitionSource={onChangeTransitionSource}
            applyFlowchartChanges={applyFlowchartChanges}
            refetchFlowchartConfig={props.refetchFlowchartConfig}
            platformFlowchartFeatures={props.flowchartEditorConfigurator.platformFlowchartFeatures}
            liveStateChanges={props.liveStateChanges}
            locked={props.locked}
            taskDefinitionsForSidepanel={props.taskDefinitionsForSidepanel}
            validationErrors={props.validationErrors}
          />
        </div>
      </ReactFlowWrapper>
    </div>
  );
}
